const CabinContent = {
  ////////
  introduction: {
    heading: "Introduction",
    paras: [
      "The customer has decided upon a cruise itinerary of destinations, sail dates, pricing, cruise ship and brand. Their next major step is to choose a cabin. We considered this the 'build' phase, directly after the 'search' phase.",
      "This phase demands a lot of decision making from the customer and has been the cause of much frustration and abandonment.",
    ],
  },
  //
  problems: {
    heading: "Understanding the problems",
    paras: [
      "Through a combination of lab-testing, user behaviour videos, and quantitative analytics, I gained insights into key pain-points for the user.",
      "Some of these included:",
    ],
    list: [
      "confusing industry jargon;",
      "lack of cabin imagery;",
      "deckplans needed for understanding cabin location;",
      "some poor UI executions, like mixing accordion flow and stepped flow.",
    ],
  },
  //
  pullquote: {
    quote:
      " The first thing I’ll be looking at is what’s included and what’s not",
    caption: "Steve, 58",
    list: [
      "one prior cruise",
      "interested in Mediteranean and Norwegian itineraries",
      "couple",
      "not committed to brand or ship",
    ],
  },
  //
  existingUserFlow: {
    heading: "The existing user flow",
    paras: [
      "The pattern for selecting a cabin mostly had 5 steps, but could vary. This evolved to meet the unique needs of over 30 cruise companies and nearly 200 ships.",
      "A typical flow could be:",
    ],
  },
  //
  ideation: {
    heading: "Ideation workshop",
    paras: [
      "We had a collaborative workshop composed of a small team from design, product, engineering, commercial, and marketing. In the workshop, each team member was very familiar with the cabin selection process, albeit from different points of view.",
      "The structure of the workshop was: ",
    ],
    list: [
      "each member wrote down problems on sticky notes;",
      "problems were grouped by similarity and prioritised by voting;",
      'a prioritised selection of problems were reframed as standardised challenges, like "How might we show where the cabin is";',
      "ideating a large quantity of solutions under a time-constraint;",
      "prioritising the solutions by vote and deciding what to execute on;",
      "agreeing on what solutions can be immediately actionable.",
    ],
  },
  //
  geurillaTest: {
    heading: "Rapid prototyping and geurilla testing",
    paras: [
      "With direction from the ideation session I quickly created an interactive prototype. This prototype focused on solving user-confusions about where on the ship cabins are located.",
      "A cruise event was coming up in a week, I wanted to have a prototype in-hand and sit with cruise customers for geurilla testing.",
      "The prototype was created to be tested first on iPad, for the convenience of a touch-device, but also because of the growing traffic on mobile devices.",
    ],
  },
  //
  prototypeCaptions: {
    screen01:
      "Accordion flow with 5 steps condensed to 3. Button remains disabled until customer makes a selection.",
    screen02:
      "Incremental pricing on the cabin cards to make it easier for customers to calculate the pricing differences.",
    screen03:
      "Expandable summary module 'builds' as the customer progresses. The pricing in the basket adjusts to reflect the customer's choices.",
    screen04:
      "The cabin 'grade' is presented with the deckplans map, as this pricing algorithm is mostly based on ship position.",
    screen05: "User can touch each deckplan cabin to surface information.",
    screen06:
      "An expanded menu shows all decks listed highest to lowest along with ship position - aft (back), mid (middle), fwd (front).",
  },
  //
  kanoChart: {
    heading: "Desirable vs feasible",
    paras: [
      "The geurilla testing I conducted, sitting directly with Iglu Cruise customers, added further insights into what we already knew from analytics and lab testing.",
      "The API for ship and cabin information was also being improved, so I continually checked feasibility with developers as I advanced the prototypes.",
      "There was a long list of potential features and changes we could introduce. As a team we had to prioritise what was desirable and what was feasible to deliver as iterative improvements.",
    ],
  },
  //
  cruiseEventCaption:
    "Setting up our stall before customers arrive and tested our prototype.",
  //
  designV1: {
    heading: "Launching the new design",
    paras: [
      "The new design kept the same flow - cabin meta > cabin type > cabin grade > promotions > cabin number.",
      "Key additions to the design were cabin imagery, deckplans, and a fully responsive layout.",
    ],
  },
  //
  designV1Captions: {
    screen01:
      "Cabin meta: this top-level category now includes imagery of the cabins along with interactive deck plans",
    screen02:
      "Cabin type: the card design contains more information about the cabins with a list of deck levels that contain this type",
    screen03:
      "Cabin grade: a summary card appears to show selections made. The deck plans 'mute' areas on the deck plans and focus on options still available",
    screen04: "Promotions: for some cruise brands this step appears first",
    screen05:
      "Cabin location: by this step most of the cabins will be 'muted' on the deck plans, focusing on specific cabins that remain as options",
    screen06: "The new design was fully responsive",
  },
  //
  pullquote02: {
    quote:
      " I've selected my cabin on the deckplan, but I'm not sure why it's not going in my basket",
    caption: "Lucinda, 48",
    list: [
      "testing on Usertesting.com",
      "has been on 3 cruises before",
      "never booked a cruise (and cabin) online",
    ],
  },
  //
  designFail: {
    heading: "A failed experiment",
    paras: [
      "After running the experiment for several weeks, it was clear that there were major issues with the design.",
      "It was hurting both online and call-center sales so it was switched off.",
      "I collaborated with Product owners to review user-behaviour videos, site analytics, and further qualitative testing to spot our errors.",
    ],
  },
  //
  designV2: {
    heading: "Second design iteration",
    paras: [
      "For the first launch we made the deckplans 'interactive' for customers to get more information only, as opposed to 'click-to-select' a cabin. This created a lot of confusion and 'cart abandonment'.",
      "From user videos and analytics there were strong indications that customers expected a cabin to be placed in a shopping cart when clicked. For this reason, we made it clearer that the deckplans were a reference only.",
      "We also reduced the steps, combining data into a single card. Initially we felt this would be overwhelming the customer. But the opposite was true - each step of the first launch did not have enough info for the customer to understand and decide.",
    ],
  },
  //
  designV2Captions: {
    screen01:
      "Cabin meta, type, grade, and promotions (4 separate steps) combined into a single card design. Deckplans appear after the initial selection is made, avoiding overwhelm of information.",
    screen02:
      "The new card design holds all the information the customer needs to make a decision. It is clearer to customers that the deck plans are for reference only.",
    screen03:
      "Mobile views - this design approach was much more successful across all device ranges.",
    screen04: "Deck plans are easily referenced on mobile",
  },
  //
  experimentResults: {
    heading: "Results for business and users",
    paras: [
      "Through iterative design, experimentation, and team collaboration I was able to deliver a design that boosted sales and helped customers get what they wanted.",
      "The solution catered for many technical challenges - variable data from 30 cruise companies and 200 ships. In addition, the design would have to support an expanding list of European and international languages, immediately catering for French, Spanish, and Italian.",
    ],
  },
};

export default CabinContent;
